import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  PhoneOff,
  PhoneIncoming,
  Mic,
  MicOff,
} from "lucide-react";
import type { CallState } from "../../hooks/useTwilioDevice";
import { setSoftPhoneHeight } from "./softPhoneStore";

interface SoftPhoneProps {
  callState: CallState;
  onHangup: () => void;
  onToggleMute: () => void;
  onAccept?: () => void;
  onReject?: () => void;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

const SoftPhone: React.FC<SoftPhoneProps> = ({
  callState,
  onHangup,
  onToggleMute,
  onAccept,
  onReject,
}) => {
  const { status, direction, remoteNumber, remoteName, duration, isMuted } =
    callState;

  const isVisible = status !== "idle";
  const displayContact = remoteName || remoteNumber || "Unknown";
  const cardRef = useRef<HTMLDivElement>(null);

  // Publish the card height so the assistant launcher and panel can sit above it
  useEffect(() => {
    if (!isVisible) {
      setSoftPhoneHeight(0);
      return;
    }
    const el = cardRef.current;
    if (!el) return;
    setSoftPhoneHeight(el.offsetHeight);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setSoftPhoneHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [isVisible, status]);

  useEffect(() => () => setSoftPhoneHeight(0), []);

  const renderIncomingRing = () => (
    <>
      <div className="px-4 py-3 bg-brand-light border-b border-brand-light rounded-t-xl">
        <p className="text-xs font-semibold text-brand uppercase tracking-wider">
          Incoming Call
        </p>
      </div>
      <div className="px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-brand-light flex items-center justify-center animate-pulse">
            <PhoneIncoming className="h-5 w-5 text-brand" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">
              {displayContact}
            </p>
            {remoteNumber && remoteName && (
              <p className="text-xs text-slate-500">{remoteNumber}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onAccept}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
          >
            <Phone className="h-4 w-4" />
            Accept
          </button>
          <button
            onClick={onReject}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
          >
            <PhoneOff className="h-4 w-4" />
            Decline
          </button>
        </div>
      </div>
    </>
  );

  const renderConnecting = () => (
    <>
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 rounded-t-xl">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          Calling...
        </p>
      </div>
      <div className="px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Phone className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">
              {displayContact}
            </p>
            <p className="text-xs text-slate-500">Connecting</p>
          </div>
        </div>
        <button
          onClick={onHangup}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
        >
          <PhoneOff className="h-4 w-4" />
          Hang Up
        </button>
      </div>
    </>
  );

  const renderRingingOutbound = () => (
    <>
      <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 rounded-t-xl">
        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
          Ringing...
        </p>
      </div>
      <div className="px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Phone className="h-5 w-5 text-blue-600 animate-pulse" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">
              {displayContact}
            </p>
            {remoteNumber && remoteName && (
              <p className="text-xs text-slate-500">{remoteNumber}</p>
            )}
          </div>
        </div>
        <button
          onClick={onHangup}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
        >
          <PhoneOff className="h-4 w-4" />
          Hang Up
        </button>
      </div>
    </>
  );

  const renderActive = () => (
    <>
      <div className="px-4 py-3 bg-brand rounded-t-xl">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-white/60 uppercase tracking-wider">
            In Call
          </p>
          <p className="text-sm font-mono font-bold text-white">
            {formatDuration(duration)}
          </p>
        </div>
      </div>
      <div className="px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-brand-light flex items-center justify-center">
            <Phone className="h-5 w-5 text-brand" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">
              {displayContact}
            </p>
            {remoteNumber && remoteName && (
              <p className="text-xs text-slate-500">{remoteNumber}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleMute}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
              isMuted
                ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {isMuted ? (
              <MicOff className="h-4 w-4" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
            {isMuted ? "Unmute" : "Mute"}
          </button>
          <button
            onClick={onHangup}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
          >
            <PhoneOff className="h-4 w-4" />
            Hang Up
          </button>
        </div>
      </div>
    </>
  );

  const renderClosed = () => (
    <>
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 rounded-t-xl">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          Call Ended
        </p>
      </div>
      <div className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
            <PhoneOff className="h-5 w-5 text-slate-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">
              {displayContact}
            </p>
            <p className="text-xs text-slate-500">
              Duration: {formatDuration(duration)}
            </p>
          </div>
        </div>
      </div>
    </>
  );

  const renderError = () => (
    <>
      <div className="px-4 py-3 bg-red-50 border-b border-red-200 rounded-t-xl">
        <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">
          Call Error
        </p>
      </div>
      <div className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
            <PhoneOff className="h-5 w-5 text-red-600" />
          </div>
          <p className="text-sm text-red-700">
            Something went wrong. Please try again.
          </p>
        </div>
      </div>
    </>
  );

  const renderBody = () => {
    if (
      status === "ringing" &&
      direction === "inbound"
    ) {
      return renderIncomingRing();
    }
    if (status === "connecting") return renderConnecting();
    if (status === "ringing" && direction === "outbound")
      return renderRingingOutbound();
    if (status === "open") return renderActive();
    if (status === "closed") return renderClosed();
    if (status === "error") return renderError();
    return null;
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={cardRef}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className="fixed bottom-6 right-6 z-50 w-72 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden"
        >
          {renderBody()}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SoftPhone;
